import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ResourceThumbnail } from '@components/attachments';
import './ResourcePreview.scss';

/**
 * ResourcePreview Component
 *
 * A reusable component for displaying a resource preview with image and basic information.
 * Used across different view pages (borrowing, consumption, refill, reservation, etc.)
 * Automatically navigates to the resource view page when clicked (if resource has an ID).
 *
 * @param {Object} props
 * @param {Object} props.resource - The resource object to display
 * @param {string} props.resource.id - Resource ID (required for navigation)
 * @param {string} props.resource.name - Resource name
 * @param {string} props.resource.description - Optional resource description
 * @param {string} props.resource.image - Optional resource image URL
 * @param {Array} props.resource.images - Optional array of resource images
 * @param {string} props.className - Additional CSS class
 * @param {Function} props.onClick - Optional custom click handler (overrides default navigation)
 * @param {boolean} props.clickable - Whether the preview should be clickable (defaults to auto-detect based on resource.id)
 * @param {string} props.size - Size variant: 'small', 'medium', 'large' (defaults to 'medium')
 * @param {boolean} props.showDescription - Whether to show the description (defaults to true)
 */
const ResourcePreview = ({
    resource,
    className = '',
    onClick,
    clickable = true,
    size = 'medium',
    showDescription = true
}) => {
    const navigate = useNavigate();

    if (!resource) {
        return null;
    }

    const handleClick = () => {
        // If a custom onClick handler is provided, use it
        if (onClick) {
            onClick(resource);
            return;
        }

        // If the resource has an ID and is clickable (or clickable is auto-determined), navigate to resource view
        if (resource.id && (clickable || !onClick)) {
            navigate(`/resources/${resource.id}`);
        }
    };

    // Determine if the component should be clickable
    // It's clickable if explicitly set to true, or if resource has an ID and no custom onClick
    const isClickable = clickable || (resource.id && !onClick);

    return (
        <div
            className={`resource-preview resource-preview--${size} ${isClickable ? 'resource-preview--clickable' : ''} ${className}`}
            onClick={handleClick}
        >
            <div className="resource-preview__image">
                <ResourceThumbnail
                    resourceId={resource.id}
                    resourceName={resource.name}
                    size="large"
                    className="preview-thumbnail"
                    thumbnailSize={300}
                />
            </div>

            <div className="resource-preview__info">
                <h3 className="resource-preview__name">{resource.name}</h3>
                {showDescription && resource.description && (
                    <p className="resource-preview__description">{resource.description}</p>
                )}
            </div>
        </div>
    );
};

export default ResourcePreview;
